import {useEffect, useRef, useState} from "react";
import IconButton from "@mui/material/IconButton";
import {CloseFullscreen, OpenInFull} from "@mui/icons-material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import {Slider} from "@mui/material";
import * as THREE from "three";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";

export default function ThreeViewport({objId, maximized, setMaximized, objRef, objLoading=undefined, isDownloading, hidden=false}) {
    const threeCanvasRef = useRef(null);
    const optionsPanelRef = useRef(null);
    const maximizeFrameRef = useRef(null);
    const sliceId = 4;
    const mouseDownRef = useRef(false);
    const mouseDownPosRef = useRef(new THREE.Vector2(0, 0));
    const sceneRef = useRef(new THREE.Scene());
    const threeRendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const pivotRef = useRef(null);
    const [objectsList, setObjectsList] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const xAxis = new THREE.Vector3(1, 0, 0);
    const yAxis = new THREE.Vector3(0, 1, 0);

    const getMaterials = (child) => Array.isArray(child.material) ? child.material : [child.material];

    useEffect(() => {
        sceneRef.current.background = new THREE.Color(0xeeeeee);
        const renderer = new THREE.WebGLRenderer({canvas: threeCanvasRef.current});
        threeRendererRef.current = renderer;
        const canvas = threeCanvasRef.current;

        const resizeToParent = () => {
            try {
                const parent = canvas?.parentNode;
                if (!parent || !threeRendererRef.current) return;
                const w = Math.max(100, parent.clientWidth || 320);
                const h = Math.max(100, parent.clientHeight || 240);
                threeRendererRef.current.setSize(w, h);
                if (cameraRef.current) {
                    cameraRef.current.aspect = w / h;
                    cameraRef.current.updateProjectionMatrix();
                }
            } catch (err) { /* defensive - ignore */ }
        };

        resizeToParent();

        let ro = null;
        if (typeof ResizeObserver !== 'undefined' && canvas?.parentNode) {
            ro = new ResizeObserver(resizeToParent);
            ro.observe(canvas.parentNode);
        } else {
            window.addEventListener('resize', resizeToParent);
        }

        if (canvas) {
            canvas.addEventListener("mousemove", onMouseMove, false);
            canvas.addEventListener("mouseup", onMouseUp, false);
            canvas.addEventListener("mousedown", onMouseDown, false);
        }

        return () => {
            if (ro && canvas?.parentNode) {
                ro.unobserve(canvas.parentNode);
                ro.disconnect();
            } else {
                window.removeEventListener('resize', resizeToParent);
            }
            if (canvas) {
                canvas.removeEventListener("mousemove", onMouseMove, false);
                canvas.removeEventListener("mouseup", onMouseUp, false);
                canvas.removeEventListener("mousedown", onMouseDown, false);
            }
            if (controlsRef.current) {
                controlsRef.current.dispose();
                controlsRef.current = null;
            }
            if (threeRendererRef.current) {
                threeRendererRef.current.setAnimationLoop(null);
                threeRendererRef.current.dispose();
                threeRendererRef.current = null;
            }
        };
    }, [])

    useEffect(() => {
        sceneRef.current.clear();
        if (objRef.current)
            addObjectToScene();
    }, [objId]);

    useEffect(() => {
        const loading = (typeof objLoading === 'boolean') ? objLoading : !!isDownloading;
        setIsUpdating(!!loading);
    }, [objLoading, isDownloading]);

    useEffect(() => {
        const renderer = threeRendererRef.current;
        if (!renderer) return;

        const canvasParent = renderer.domElement?.parentNode;
        const optionsEl = optionsPanelRef.current;

        const applySize = (w, h) => {
            if (!canvasParent) return;
            try { renderer.setSize(w, h); } catch (err) {}
            if (cameraRef.current) {
                cameraRef.current.aspect = w / h;
                cameraRef.current.updateProjectionMatrix();
                cameraRef.current.lookAt(0, 0, 0);
            }
            if (controlsRef.current) {
                controlsRef.current.target.set(0, 0, 0);
                controlsRef.current.update();
            }
        };

        // Use ResizeObserver to handle sizing dynamically
        const resizeToParent = () => {
            if (!canvasParent) return;
            const w = canvasParent.clientWidth || 320;
            const h = canvasParent.clientHeight || 240;
            if (w > 0 && h > 0) {
                applySize(w, h);
            }
        };

        maximizeFrameRef.current = requestAnimationFrame(resizeToParent);

        if (canvasParent) canvasParent.hidden = false;
        if (optionsEl) optionsEl.style.display = 'block';

        window.addEventListener('resize', resizeToParent);
        return () => {
            window.removeEventListener('resize', resizeToParent);
            if (maximizeFrameRef.current !== null) {
                cancelAnimationFrame(maximizeFrameRef.current);
                maximizeFrameRef.current = null;
            }
        };
    }, [maximized]);

    const opacityChange = (idx, value) => {
        const newOl = objectsList.map(o => Object.assign({}, o));
        if (!newOl[idx] || !objRef.current?.children?.[newOl[idx].index]) return;
        const targetObject = objRef.current.children[newOl[idx].index];
        const targetMaterials = getMaterials(targetObject);
        newOl[idx].opacity = value;
        newOl[idx].visible = value !== 0;
        setObjectsList(newOl);
        if (value === 0) {
            targetObject.visible = false;
        } else {
            targetObject.visible = true;
            targetMaterials.forEach((material) => {
                material.blendSrc = THREE.OneFactor;
            });
            if (value === 100) {
                targetMaterials.forEach((material) => {
                    material.transparent = false;
                    material.depthWrite = true;
                    material.blendDst = THREE.ZeroFactor;
                });
                targetObject.renderOrder = -1;
            } else {
                targetMaterials.forEach((material) => {
                    material.transparent = true;
                    material.depthWrite = false;
                    material.blendDst = THREE.OneMinusSrcAlphaFactor;
                });
                targetObject.renderOrder = 3000;
            }
        }
        targetMaterials.forEach((material) => {
            material.opacity = value / 100;
        });
    }

    const onMouseDown = (e) => {
        mouseDownRef.current = true;
        mouseDownPosRef.current.x = e.clientX;
        mouseDownPosRef.current.y = e.clientY;
    }

    const onMouseUp = () => {
        mouseDownRef.current = false;
    }

    const onMouseMove = (e) => {
        e.preventDefault();
        if (!mouseDownRef.current || !pivotRef.current) return;
        const deltaX = e.clientX - mouseDownPosRef.current.x;
        const deltaY = e.clientY - mouseDownPosRef.current.y;
        mouseDownPosRef.current.x = e.clientX;
        mouseDownPosRef.current.y = e.clientY;
        if (e.buttons === 1) {
            pivotRef.current.rotateOnWorldAxis(xAxis, -deltaY / 100);
            pivotRef.current.rotateOnWorldAxis(yAxis, deltaX / 100);
        }
    }

    const addObjectToScene = async () => {
        const canvas = threeRendererRef.current.domElement;
        const aspect = (canvas && canvas.width && canvas.height) ? (canvas.width / canvas.height) : 1;
        const camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 3000);
        camera.position.z -= 300;
        cameraRef.current = camera;
        removeCameraFromObject();
        setupPivot();
        const color = 0xFFFFFF;
        addAmbientLight(color, 1);
        addCameraLight(camera, color, 1);
        addDirectionalLight(color, 1);
        materialSetup();
        let controls = new OrbitControls(camera, threeCanvasRef.current);
        controls.target.set(0, 0, 0);
        controls.enableRotate = false;
        controlsRef.current = controls;
        const animate = () => {
            controls.update();
            threeRendererRef.current.render(sceneRef.current, camera);
        }
        threeRendererRef.current.setAnimationLoop(animate);
        sceneRef.current.add(camera);
        setIsUpdating(false);
    }

    const removeCameraFromObject = () => {
        const currentObj = objRef.current;
        currentObj.children = currentObj.children.filter((obj) => obj?.geometry);
    }

    const setupPivot = () => {
        const currentObj = objRef.current;
        if (!currentObj || currentObj.children.length === 0) {
            setObjectsList([]);
            pivotRef.current = null;
            return;
        }
        const pivot = new THREE.Object3D();
        pivot.position.set(0, 0, 0);
        pivot.add(currentObj);
        const ncs = new THREE.Vector3(0, 0, 0);
        const objList = [];
        currentObj.children.forEach((child, idx) => {
            const materials = getMaterials(child);
            const color = materials[0]?.color;
            objList.push({
                name: child.name.replaceAll('_', ' '), index: idx,
                opacity: (materials[0]?.opacity ?? 1) * 100,
                visible: child.visible !== false,
                colorHex: color?.isColor ? `#${color.getHexString()}` : '',
            });
            child.geometry.computeBoundingSphere();
            ncs.add(child.geometry.boundingSphere.center);
        })
        setObjectsList(objList);
        const newCenter = ncs.divideScalar(currentObj.children.length).multiplyScalar(-1);
        currentObj.position.set(newCenter.x, newCenter.y, newCenter.z);
        pivot.rotateOnWorldAxis(xAxis, THREE.MathUtils.degToRad(-90));
        pivotRef.current = pivot;
        sceneRef.current.add(pivot);
    }

    const addAmbientLight = (color, intensity) => {
        const light = new THREE.AmbientLight(color, intensity);
        sceneRef.current.add(light);
    }

    const addCameraLight = (camera, color, intensity) => {
        const camLight = new THREE.SpotLight(color, intensity, 0, Math.PI, 0, 0);
        camLight.target = pivotRef.current;
        camera.add(camLight);
    }

    const addDirectionalLight = (color, intensity) => {
        const dirLight = new THREE.DirectionalLight(color, intensity);
        dirLight.position.y = 20;
        dirLight.target = pivotRef.current;
        sceneRef.current.add(dirLight);
    }

    const materialSetup = () => {
        const encountered = [];
        objRef.current.children.forEach((child) => {
            child.castShadow = true;
            child.receiveShadow = true;
            const materials = getMaterials(child).map((material) => {
                if (encountered.includes(material.uuid)) {
                    return material.clone();
                }
                encountered.push(material.uuid);
                return material;
            });
            child.material = Array.isArray(child.material) ? materials : materials[0];
            materials.forEach((material) => {
                if ('shininess' in material) material.shininess = 0;
                material.transparent = true;
            });
        })
    }

    const isMaximized = maximized === sliceId;

    return (
        <Stack 
            direction="row" 
            justifyContent="center" 
            sx={{
                display: hidden ? 'none' : '',
                width: '100%',
                height: '100%',
                alignItems: 'stretch',
                gap: 'min(1vw, 8px)'
            }}
        >
            <div style={{position: 'relative', flex: isMaximized ? '2 1 66.666%' : '2 1 66.666%', height: '100%', minWidth: 0}}>
                <div style={{
                    display: (isUpdating || !objRef.current) ? 'flex' : 'none',
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    zIndex: 3,
                    pointerEvents: 'auto'
                }}>
                    <CircularProgress disableShrink />
                </div>

                <canvas ref={threeCanvasRef} style={{position: 'absolute', width: '100%', height: '100%'}}/>
                <IconButton sx={{
                    position: 'absolute', top: '10px', right: '10px', color: "rgba(255, 255, 255, 0.9)",
                    backgroundColor: "rgba(150,150,150,0.5)", 'z-index': 1, '&:hover': {
                        backgroundColor: "rgba(150, 150, 150, 0.5)"
                    }
                }} onClick={() => maximized === sliceId ? setMaximized(-1) : setMaximized(sliceId)}>
                    {maximized === sliceId ? <CloseFullscreen/> : <OpenInFull/>}
                </IconButton>
            </div>
            <div ref={optionsPanelRef} style={{position: 'relative', flex: '1 1 33.333%', height: '100%', minWidth: 0, overflow: 'hidden'}}>
                <div style={{
                    display: (isUpdating || !objRef.current) ? 'flex' : 'none',
                    position: 'absolute',
                    inset: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    zIndex: 3,
                    pointerEvents: 'auto'
                }}>
                    <CircularProgress disableShrink />
                </div>

                <List sx={{
                    width: '100%',
                    maxWidth: '100%',
                    backgroundColor: "#eeeeee",
                    position: 'relative',
                    overflow: 'auto',
                    height: '100%',
                    maxHeight: '100%',
                    display: 'block',
                    '& ul': {padding: 0},
                }}>
                    {objectsList.map((structure, index) => (
                        <ListItem
                            key={index}
                            data-structure-name={structure.name}
                            data-structure-visible={String(structure.visible)}
                            data-structure-color={structure.colorHex}
                            sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 1}}
                        >
                            <ListItemText primary={structure.name} secondary={`${structure.opacity} %`} sx={{margin: 0, mb: 0.5}}/>
                            <Slider value={structure.opacity} onChange={(_, value) => opacityChange(index, value)} name={index.toString()} sx={{width: '100%'}}/>
                        </ListItem>
                    ))}
                </List>
            </div>
        </Stack>
    )
}